/**
 * Agent Chat WebSocket 客户端封装（Vue 2 / 原生 WebSocket）
 *
 * 用于 user-client-common-pc 中 agent-chat.vue 的 AI 对话 WebSocket 接入。
 *
 * 灰度开关：`src/apis/config.js` 中的 `useWsAgentChat`
 *   - true  → 走本封装（Node WS，chatType='agent'）
 *   - false → 走旧 `saveMsg + sendOpen(SSE)` 路径
 * 双轨运行至少 2 周，稳定后删除旧 SSE 路径。
 *
 * 协议（与 Node AgentSession 对齐）：
 *   客户端 → 服务端
 *     { type:'send_message', chatType:'agent', content, sessionGuid, lastMsgId,
 *       imgs, videos, fileUrls, aiModelGuid, token, app_type, app_guid, ... }
 *     { type:'pong', ts }   // 收到服务端 ping 时响应
 *     { type:'ping' }       // 心跳保活（客户端主动发，服务端回 pong）
 *   服务端 → 客户端
 *     { type:'text_chunk',       content }
 *     { type:'tool_start',       toolName, label? }
 *     { type:'tool_done',        toolName?, result? }
 *     { type:'structured_data',  data }
 *     { type:'done',             msgId }
 *     { type:'error',            message }
 *     { type:'ping' }       // 服务端主动发的心跳（需要回 pong）
 *     { type:'pong', ts }   // 响应客户端 ping
 *
 * 使用示例：
 *   const ws = new AgentChatWs({ url: config.wsUrl })
 *   ws.on('text_chunk', ev => {})
 *   ws.on('done',       ev => {})
 *   ws.connect()
 *   ws.sendMessage({ sessionGuid, content, token, app_guid, ... })
 *   // 页面卸载时
 *   ws.close()
 */

const DEFAULT_HEARTBEAT_INTERVAL = 25000 // 25s 主动发 ping
const MAX_RECONNECT_ATTEMPTS = 3
const BASE_RECONNECT_DELAY = 1000 // 指数退避基数 1s

class AgentChatWs {
  constructor(options = {}) {
    this.url = options.url || ''
    this.heartbeatInterval = options.heartbeatInterval || DEFAULT_HEARTBEAT_INTERVAL
    this.maxReconnectAttempts = options.maxReconnectAttempts != null ? options.maxReconnectAttempts : MAX_RECONNECT_ATTEMPTS

    this.ws = null
    this.connected = false
    this.reconnectAttempts = 0
    this.reconnectTimer = null
    this.heartbeatTimer = null
    this.manuallyClosed = false

    // 事件订阅表：eventType → [handler, ...]
    this._handlers = {}
  }

  /**
   * 订阅事件
   * @param {string} type 事件类型（text_chunk / tool_start / tool_done / structured_data / done / error / open / close）
   * @param {(payload:any)=>void} handler
   */
  on(type, handler) {
    if (!this._handlers[type]) this._handlers[type] = []
    this._handlers[type].push(handler)
    return this
  }

  off(type, handler) {
    if (!this._handlers[type]) return this
    if (!handler) {
      this._handlers[type] = []
    } else {
      this._handlers[type] = this._handlers[type].filter(h => h !== handler)
    }
    return this
  }

  _emit(type, payload) {
    const list = this._handlers[type] || []
    for (const h of list) {
      try { h(payload) } catch (e) { console.error('[AgentChatWs] handler error:', e) }
    }
  }

  /** 建立连接（幂等，已连接/正在连接时直接返回） */
  connect() {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return
    }
    if (!this.url) {
      console.error('[AgentChatWs] url is empty')
      return
    }

    this.manuallyClosed = false
    this.ws = new WebSocket(this.url)

    this.ws.onopen = () => {
      this.connected = true
      this.reconnectAttempts = 0
      this._startHeartbeat()
      this._emit('open')
    }

    this.ws.onmessage = (e) => {
      let event
      try { event = JSON.parse(e.data) } catch { return }

      // 心跳：服务端主动发 ping 时回 pong
      if (event.type === 'ping') {
        this._safeSend({ type: 'pong', ts: Date.now() })
        return
      }
      // pong：更新存活标记即可，不透传业务层
      if (event.type === 'pong') {
        return
      }

      this._emit(event.type, event)
    }

    this.ws.onclose = () => {
      this.connected = false
      this._stopHeartbeat()
      this._emit('close')
      if (!this.manuallyClosed) {
        this._scheduleReconnect()
      }
    }

    this.ws.onerror = (err) => {
      // onerror 紧接着会触发 onclose，重连统一在 onclose 里调度
      this._emit('error', { message: '连接出错', raw: err })
    }
  }

  /**
   * 发送 send_message（chatType 固定为 'agent'）
   * @param {object} params
   * @param {string} params.sessionGuid   会话轮次 guid（agent 场景即 chatLunciGuid）
   * @param {string} params.content       用户文本
   * @param {string} [params.lastMsgId]   上一条消息 id
   * @param {string[]} [params.imgs]
   * @param {string[]} [params.videos]
   * @param {Array}    [params.fileUrls]
   * @param {string}   [params.aiModelGuid]
   * @param {string}   params.token
   * @param {string}   [params.app_type]  默认 'xiaoyipc'
   * @param {string}   params.app_guid    merchantGuid
   * @returns {boolean} 是否发送成功（未连接时返回 false，由调用方决定重连策略）
   */
  sendMessage(params) {
    const payload = {
      type: 'send_message',
      chatType: 'agent',
      content: params.content || '',
      sessionGuid: params.sessionGuid || '',
      lastMsgId: params.lastMsgId || '',
      imgs: params.imgs || [],
      videos: params.videos || [],
      fileUrls: params.fileUrls || [],
      aiModelGuid: params.aiModelGuid || '',
      token: params.token || '',
      app_type: params.app_type || 'xiaoyipc',
      app_guid: params.app_guid || '',
    }
    // 允许透传其他可选字段（sceneValue / enableThinking / systemPrompt 等）
    if (params.sceneValue !== undefined) payload.sceneValue = params.sceneValue
    if (params.enableThinking !== undefined) payload.enableThinking = params.enableThinking
    if (params.temperature !== undefined) payload.temperature = params.temperature
    if (params.topP !== undefined) payload.topP = params.topP
    if (params.maxOutputTokens !== undefined) payload.maxOutputTokens = params.maxOutputTokens
    if (params.systemPrompt !== undefined) payload.systemPrompt = params.systemPrompt

    return this._safeSend(payload)
  }

  /** 主动关闭（页面卸载 / 组件 destroy 时调用） */
  close() {
    this.manuallyClosed = true
    this._stopHeartbeat()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      try { this.ws.close() } catch (_) { /* ignore */ }
      this.ws = null
    }
    this.connected = false
  }

  /** 当前是否可发送 */
  isOpen() {
    return !!(this.ws && this.ws.readyState === WebSocket.OPEN)
  }

  // ───────────────────────── 内部工具 ─────────────────────────

  _safeSend(obj) {
    if (!this.isOpen()) return false
    try {
      this.ws.send(JSON.stringify(obj))
      return true
    } catch (e) {
      console.error('[AgentChatWs] send failed:', e)
      return false
    }
  }

  _startHeartbeat() {
    this._stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      this._safeSend({ type: 'ping' })
    }, this.heartbeatInterval)
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  _scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this._emit('error', { message: '网络连接已断开，请手动重试' })
      return
    }
    // 指数退避：1s / 2s / 4s
    const delay = BASE_RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts)
    this.reconnectAttempts += 1
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = setTimeout(() => {
      this.connect()
    }, delay)
  }
}

export default AgentChatWs
export { AgentChatWs }
